import React from 'react';
import {
  usePpVisitedCountsQuery,
  PpBarChartParams,
} from '../Pallipaarvai.service';

export interface VisitedCounts {
  visited_officials_count: number;
  not_visited_officials_count: number;
  observation_done_count: number;
  schools_visited_count: number;
  schools_not_visited_count: number;
  total_schools_count: number;
  total_officials_count: number;
  overall_target_achieved: number;
}

interface VistedCntsCardProps {
  filterParams: PpBarChartParams;
}

const cardConfig = [
  {
    key: 'total_schools_count',
    label: 'Total Schools',
    color: '#4338ca',
    background: 'aliceblue',
  },
  {
    key: 'schools_visited_count',
    label: 'Schools Visited',
    color: '#16a34a',
    background: '#f0fff1',
  },
  {
    key: 'schools_not_visited_count',
    label: 'Schools Not Visited',
    color: '#dc2626',
    background: '#fff2f0ff',
  },
  {
    key: 'total_officials_count',
    label: 'Total Officials',
    color: '#2563eb',
    background: '#f0f7ffff',
  },
  {
    key: 'visited_officials_count',
    label: 'Officials Visited',
    color: '#059669',
    background: '#f0fff7ff',
  },
  {
    key: 'not_visited_officials_count',
    label: 'Officials Not Visited',
    color: '#e11d48',
    background: '#fff2f0ff',
  },
  {
    key: 'observation_done_count',
    label: 'Observation Done',
    color: '#7c3aed',
    background: '#f9f0ffff',
  },
] as const;

const CircularProgress: React.FC<{ percentage: number }> = ({ percentage }) => {
  const radius = 60;
  const stroke = 12;

  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <svg height={radius * 2} width={radius * 2}>
      {/* Background circle */}
      <circle
        stroke="#334155"
        fill="transparent"
        strokeWidth={stroke}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
      />
      {/* Progress circle */}
      <circle
        stroke="#facc15"
        fill="transparent"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={strokeDashoffset}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
        style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
      />
      {/* Percentage text */}
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dy="0.35em"
        fill="#ffffff"
        fontSize="17"
        fontWeight="bold"
      >
        {percentage}%
      </text>
    </svg>
  );
};

const VistedCntsCard: React.FC<VistedCntsCardProps> = ({ filterParams }) => {
  const { data: res, isLoading } = usePpVisitedCountsQuery(filterParams);
  const data = res?.result as VisitedCounts | null;

  const targetAchieved = data?.overall_target_achieved ?? 0;
  const schoolsVisited = data?.schools_visited_count ?? 0;
  const totalSchools = data?.total_schools_count ?? 0;
  const notVisited = data?.schools_not_visited_count ?? 0;
  const officialsPending = data?.not_visited_officials_count ?? 0;

  return (
    <>
      <div className="rounded-3 p-3 mt-3 d-flex align-items-center justify-content-between bg-primary">
        {/* Left section: Title */}
        {/* <div>
          <h6 className="text-white fw-bold mb-0">Overall Observation</h6>
        </div> */}

        {/* Middle section: Progress ring + text */}
        <div className="d-flex align-items-center gap-3">
          <CircularProgress percentage={isLoading ? 0 : targetAchieved} />
          <div>
            <small className="text-white mb-0" style={{ fontSize: '16px' }}>
              Target achieved · state-wide
            </small>
            <p
              className="text-white fw-semibold mb-0"
              style={{ fontSize: '20px' }}
            >
              {isLoading
                ? '...'
                : `${schoolsVisited.toLocaleString()} of ${totalSchools.toLocaleString()} schools observed this session`}
            </p>
          </div>
        </div>

        {/* Right section: Stats */}
        <div className="d-flex align-items-center gap-4">
          <div className="text-center">
            <h5 className="text-white fw-bold mb-0">
              {isLoading ? '...' : notVisited.toLocaleString()}
            </h5>
            <small
              className="text-white text-uppercase"
              style={{ fontSize: '10px', letterSpacing: '0.5px' }}
            >
              Not Visited
            </small>
          </div>
          <div className="text-center">
            <h5 className="text-white fw-bold mb-0">
              {isLoading ? '...' : officialsPending.toLocaleString()}
            </h5>
            <small
              className="text-white text-uppercase"
              style={{ fontSize: '10px', letterSpacing: '0.5px' }}
            >
              Officials Pending
            </small>
          </div>
          <div className="text-center">
            <h5 className="text-white fw-bold mb-0">38</h5>
            <small
              className="text-white text-uppercase"
              style={{ fontSize: '10px', letterSpacing: '0.5px' }}
            >
              Districts Tracked
            </small>
          </div>
        </div>
      </div>

      <div className="row g-3 mt-3">
        {cardConfig.map((card) => (
          <div key={card.key} className="col-12 col-md-4 col-lg-3">
            <div
              className="card text-center p-3 h-100"
              style={{
                borderTop: `3px solid ${card.color}`,
                backgroundColor: card.background,
              }}
            >
              <h6 className="text-muted mb-2" style={{ fontSize: '13px' }}>
                {card.label}
              </h6>
              <h4 className="mb-0 fw-bold">
                {isLoading ? '...' : (data?.[card.key] ?? 0).toLocaleString()}
              </h4>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default VistedCntsCard;
